import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { SortingState, Row } from "@tanstack/react-table";
import { PartnerContactColumn, PartnerContactData } from "./types";
import DataTable from "@/components/controls/DataTable/DataTable";
import {
  GroupRankResolvers,
  statusRank
} from "@/entities/dataTables/groupOrdering";
import NewQuickView from "@/components/layouts/NewQuickView/NewQuickView";
import { useNewQuickViewDrawer } from "@/services/hooks/entities/useNewQuickViewDrawer";
import { UserData } from "@/entities/users/types";
import {
  ComputedFields,
  EmptyFilters,
  GenericAggregations
} from "@/services/api/types";
import { createDataMapById, flattenDataByKeys } from "@/utils/datamaps";
import {
  usePartnerContactsInfinite,
  usePartnerContactsDistinctFilterOptions,
  useResetPartnerContactsInfinite
} from "./hooks";
import EmptyState from "@/components/layouts/PeopleTab/components/EmptyState";
import CreateContactModal from "@/components/layouts/CreateContactModal";
import { usePartners } from "../hooks";
import { PartnerData, PartnerStatus } from "../types";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import {
  EmptyView,
  ViewDataConfiguration,
  ViewType
} from "@/entities/views/types";
import ToolbarActions from "@/components/controls/ToolbarActions/ToolbarActions";
import { ColumnData } from "@/entities/dataTables/types";
import {
  getDefaultColumnsStateWithCustomFields,
  getPageNameFromTableId,
  getTempView
} from "@/entities/dataTables/helpers";
import {
  partnerContactColumnKeys,
  partnerContactsGroupByOptions,
  partnerContactsSortByOptions,
  partnerStatusColors
} from "./helpers";
import { usePartnerContactColumns } from "./columns";
import { usePartnerContactFilterOptions } from "./filters";
import { isEmpty, isEqual } from "lodash";
import PartnerContactsBottomKPIBar from "./components/PartnerContactsBottomKPIBar";
import { useFieldVisibility, useEntityFields } from "@/pages/settings/hooks";
import DataTableToolbar from "@/components/controls/DataTableToolbar";
import { useEntityExport } from "@/services/hooks/entities/useEntityExport";
import {
  ExportStateChangeHandler,
  useNotifyExportState
} from "@/services/hooks/entities/useExportButtonState";
import { buildGroupSummaries } from "@/components/controls/DataTable/groupSummaries";
import { StatusBadge } from "@/components/ui/status-badge";
import { getEnumTKey } from "@/utils/strings";

const TABLE_ID = "partnercontacts-table";

interface Props {
  partnerId?: string;
  userData?: UserData;
  onExportStateChange?: ExportStateChangeHandler;
}

// Status groups follow the order its enum declares, not whichever column is sorted.
const GROUP_RANK_RESOLVERS: GroupRankResolvers<PartnerContactData> = {
  [PartnerContactColumn.STATUS]: statusRank
};

/**
 * The stored column behind each group option the toolbar offers. The server
 * counts every group over the whole filtered set from these, so a group header
 * reads the same on the first page as on the last — the partner column stores
 * its own name, so it groups on the value the cell already shows.
 */
const SERVER_GROUP_FIELD: Record<string, keyof PartnerContactData> = {
  [PartnerContactColumn.PARTNER_NAME]: "partnerName",
  [PartnerContactColumn.STATUS]: "status",
  [PartnerContactColumn.COUNTRY]: "country",
  [PartnerContactColumn.STATE_REGION]: "stateRegion"
};

const PartnerContacts: React.FC<Props> = ({
  partnerId,
  userData,
  onExportStateChange
}) => {
  const { t } = useTranslation();
  const {
    drawerState: newDrawerState,
    openDrawer: newOpenDrawer,
    closeDrawer: newCloseDrawer
  } = useNewQuickViewDrawer();

  const onRowClick = useCallback(
    (contact: PartnerContactData) => {
      newOpenDrawer(contact.id, EntityType.PARTNER_CONTACT);
    },
    [newOpenDrawer]
  );
  const baseFilters = useMemo(
    () =>
      partnerId
        ? { [PartnerContactColumn.PARTNER]: [partnerId] }
        : EmptyFilters,
    [partnerId]
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState(baseFilters);
  const [isCreateContactOpen, setIsCreateContactOpen] = useState(false);
  const resetPartnerContacts = useResetPartnerContactsInfinite();
  const [sorting, onSortingChange] = useState<SortingState>([
    { id: PartnerContactColumn.FULL_NAME, desc: false }
  ]);
  const [columnsState, setColumnsState] = useState<ColumnData[]>([]);
  const [groupBy, setGroupBy] = useState<string[]>([]);

  const [selectedView, setSelectedView] = useState<ViewDataConfiguration>(
    EmptyView(ViewType.PARTNER_CONTACTS_TABLE)
  );

  const { data: entityFieldsResponse } = useEntityFields(
    EntityType.PARTNER_CONTACT
  );
  const customFields = entityFieldsResponse?.fields || [];
  const {
    isFieldVisible,
    filterVisibleColumnKeys,
    isLoaded: fieldsLoaded
  } = useFieldVisibility(EntityType.PARTNER_CONTACT);
  const scopedColumnKeys = useMemo(
    () =>
      partnerId
        ? partnerContactColumnKeys.filter(
            (key) => key !== PartnerContactColumn.PARTNER_NAME
          )
        : partnerContactColumnKeys,
    [partnerId]
  );
  const activeColumnKeys = useMemo(
    () => filterVisibleColumnKeys(scopedColumnKeys),
    [filterVisibleColumnKeys, scopedColumnKeys]
  );
  // Every row already belongs to the same partner when scoped, so grouping by
  // it would put the whole table under one redundant header.
  const scopedGroupByOptions = partnerId ? [] : partnerContactsGroupByOptions;

  const defaultColumnsState = useMemo(
    () =>
      getDefaultColumnsStateWithCustomFields(
        activeColumnKeys,
        customFields,
        getPageNameFromTableId(TABLE_ID),
        t
      ),
    [activeColumnKeys, customFields, t]
  );

  // Opening a view saved without columns empties the layout; re-seed it then
  // too, still only once the custom fields have loaded.
  const isColumnsStateEmpty = columnsState.length === 0;
  useEffect(() => {
    if (!entityFieldsResponse) return;
    setColumnsState((prev) => (prev.length === 0 ? defaultColumnsState : prev));
  }, [defaultColumnsState, isColumnsStateEmpty, entityFieldsResponse]);

  const handleColumnsChange = useCallback((updatedColumns?: ColumnData[]) => {
    if (updatedColumns) {
      setColumnsState(updatedColumns);
    }
  }, []);

  const sortParams = useMemo(() => {
    const sortId = sorting[0].id ?? PartnerContactColumn.FULL_NAME;
    // The name column sorts on the synthetic fullName id (backend sorts on
    // firstName). Every other column and custom field sorts on its own id.
    return {
      field: sortId === PartnerContactColumn.FULL_NAME ? "firstName" : sortId,
      asc: !sorting[0].desc
    };
  }, [sorting]);

  const groupField = groupBy[0];
  const serverGroupField = groupField
    ? SERVER_GROUP_FIELD[groupField]
    : undefined;

  const {
    entities: partnerContacts,
    hasNextPage,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    isError,
    refetch,
    total,
    aggregations,
    isAggregationsLoading
  } = usePartnerContactsInfinite(
    filters,
    searchTerm ? { term: searchTerm } : undefined,
    serverGroupField,
    undefined,
    sortParams.field,
    sortParams.asc,
    undefined,
    true,
    ComputedFields.MINIMAL
  );

  /** Grouped as requested — one entry per distinct value of the group field. */
  const groupSummaries = useMemo(() => {
    if (!serverGroupField) return undefined;
    return buildGroupSummaries(
      aggregations as GenericAggregations<string> | undefined,
      [],
      isAggregationsLoading
    );
  }, [serverGroupField, aggregations, isAggregationsLoading]);

  // Every group the response named is on screen from the first page, with the
  // count the server gave it — including one whose contacts have not been paged
  // in yet, which the loaded rows alone could not have revealed.
  const contactGroups = useMemo(() => {
    if (!groupSummaries) return undefined;

    if (groupField === PartnerContactColumn.STATUS) {
      // Statuses read in the order their enum declares them, and keep the badge
      // the column itself shows.
      return Object.keys(groupSummaries)
        .sort((a, b) => Number(a) - Number(b))
        .map((value) => {
          const status = Number(value) as PartnerStatus;
          return {
            value,
            label: PartnerStatus[status] ? (
              <StatusBadge statusColor={partnerStatusColors[status]}>
                {t(
                  getEnumTKey(
                    "partnercontacts",
                    "statusValues",
                    PartnerStatus,
                    status
                  )
                )}
              </StatusBadge>
            ) : (
              t("tables.notAvailable")
            )
          };
        });
    }

    return Object.keys(groupSummaries)
      .sort((a, b) => a.localeCompare(b))
      .map((value) => ({ value, label: value || t("tables.notAvailable") }));
  }, [groupSummaries, groupField, t]);

  const partnerIds: string[] = useMemo(
    () =>
      flattenDataByKeys(partnerContacts, (contact: PartnerContactData) => [
        contact.partner
      ]),
    [partnerContacts]
  );
  const partnersData = usePartners(partnerIds);
  const partnersDataMap = useMemo(
    () =>
      partnersData.entities
        ? createDataMapById(partnersData.entities as PartnerData[])
        : {},
    [partnersData.entities]
  );

  const { data: countryData } = usePartnerContactsDistinctFilterOptions(
    PartnerContactColumn.COUNTRY
  );
  const { data: stateRegionData } = usePartnerContactsDistinctFilterOptions(
    PartnerContactColumn.STATE_REGION
  );

  const filterOptions = usePartnerContactFilterOptions({
    countryValues: countryData?.values || [],
    stateRegionValues: stateRegionData?.values || []
  });

  const exportSorting = useMemo<SortingState>(
    () => [{ id: sortParams.field, desc: !sortParams.asc }],
    [sortParams]
  );

  const { exportTo: exportPartnerContacts, isExporting: isExportingContacts } =
    useEntityExport({
    createRoute: "partners_contacts/export/create",
    statusRoute: "partners_contacts/export/status",
    tableName: "partner_contacts",
    filters,
    sorting: exportSorting,
    searchTerm,
    columnsState,
    totalRows: total,
    getFilterOptions: () => filterOptions
  });

  useNotifyExportState(
    onExportStateChange,
    exportPartnerContacts,
    isExportingContacts
  );

  useEffect(() => {
    // When scoped to a partner (detail-view People tab), keep the partner
    // filter authoritative and skip standalone-table saved views.
    if (partnerId) return;
    const tempView = getTempView(selectedView);
    if (tempView?.config?.filters) {
      setFilters(tempView.config.filters);
    }
    if (tempView?.config?.searchTerm) {
      setSearchTerm(tempView.config.searchTerm);
    }
    if (tempView?.config?.groupBy) {
      setGroupBy(tempView.config.groupBy);
    }
  }, [selectedView, partnerId]);

  const handleLoadMore = () => {
    if (!isFetchingNextPage && hasNextPage && !isError) {
      fetchNextPage();
    }
  };

  const selectedColumns = useMemo(() => {
    if (columnsState.length > 0) {
      return columnsState;
    }
    const tempView = getTempView(selectedView);
    return tempView?.config?.columns || selectedView?.config?.columns || [];
  }, [columnsState, selectedView]);

  const columns = usePartnerContactColumns({
    partnersDataMap,
    selectedColumns,
    customFields,
    isFieldVisible,
    fieldsLoaded
  });

  const showPartnerEmptyState =
    !!partnerId && isEmpty(searchTerm) && isEqual(filters, baseFilters);

  // Once the empty state is the whole tab, search, filters, column display and
  // a zeroed KPI bar have nothing to act on. They come back as soon as a filter
  // or search is what emptied the table, so the user can still undo it.
  const showEmptyStateOnly =
    showPartnerEmptyState && !isLoading && !isError && isEmpty(partnerContacts);

  return (
    <>
      {!partnerId ? (
        <DataTableToolbar
          viewType={ViewType.PARTNER_CONTACTS_TABLE}
          selectedView={selectedView}
          setSelectedView={setSelectedView}
          filterOptions={filterOptions}
          filters={filters}
          setFilters={setFilters}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          groupBy={groupBy}
          setGroupBy={setGroupBy}
          sorting={sorting}
          defaultColumnsState={defaultColumnsState}
          columnsState={columnsState}
          setColumnsState={setColumnsState}
          sortByOptions={partnerContactsSortByOptions}
          defaultSortByValue="firstName"
          onSortingChange={onSortingChange}
          tableId={TABLE_ID}
          onColumnsChange={handleColumnsChange}
          customFields={customFields}
        />
      ) : null}
      <ToolbarActions
        className={showEmptyStateOnly ? "hidden" : "p-2 pr-4"}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        filters={filters}
        setFilters={setFilters}
        filterOptions={filterOptions}
        baseFilters={baseFilters}
        groupBy={groupBy}
        setGroupBy={setGroupBy}
        groupByOptions={scopedGroupByOptions}
        sorting={sorting}
        onSortingChange={onSortingChange}
        sortByOptions={partnerContactsSortByOptions}
        defaultSortByValue="firstName"
        columnsState={columnsState}
        setColumnsState={setColumnsState}
        defaultColumnsState={defaultColumnsState}
        selectedView={selectedView}
        setSelectedView={setSelectedView}
        viewType={ViewType.PARTNER_CONTACTS_TABLE}
        onColumnsChange={handleColumnsChange}
        tableId={TABLE_ID}
        customFields={customFields}
      />
      <DataTable
        groupRankResolvers={GROUP_RANK_RESOLVERS}
        key={TABLE_ID}
        dataTest={TABLE_ID}
        columns={columns}
        initialSortBy={sorting}
        manualSorting
        onSortingChange={onSortingChange}
        data={partnerContacts}
        headerClassName="border-none"
        stickyLeftColumn
        resizableColumns
        className="h-full"
        isInfiniteScroll
        isLoadingMore={isFetchingNextPage}
        isDataLoading={isLoading}
        hasMore={hasNextPage}
        onLoadMore={handleLoadMore}
        loadingThreshold={100}
        groupBy={groupBy}
        groupingConfig={{
          typeLabel: t("partnercontacts.bottomKPI.total"),
          groups: contactGroups,
          groupSummaries
        }}
        enableColumnReorder
        tableId={TABLE_ID}
        selectedView={selectedView}
        columnsState={columnsState}
        onColumnsChange={handleColumnsChange}
        filterOptions={filterOptions}
        filters={filters}
        setFilters={setFilters}
        hasActiveFilters={!isEqual(filters, baseFilters)}
        onClearFilters={() => setFilters(baseFilters)}
        onClearSearch={() => setSearchTerm("")}
        isError={isError}
        onRetry={refetch}
        onRowClick={(row: Row<PartnerContactData>) => onRowClick(row.original)}
        customEmptyState={
          showPartnerEmptyState ? (
            <EmptyState
              entityType={EntityType.PARTNER}
              onAddPeople={() => setIsCreateContactOpen(true)}
              className="pt-0"
            />
          ) : undefined
        }
      />
      {showEmptyStateOnly ? null : (
        <PartnerContactsBottomKPIBar totalPartnerContacts={total} />
      )}
      <NewQuickView
        isOpen={newDrawerState.isOpen}
        entityId={newDrawerState.entityId}
        entityType={EntityType.PARTNER_CONTACT}
        onClose={newCloseDrawer}
        userData={userData}
        isTabView
      />
      {partnerId && (
        <CreateContactModal
          isOpen={isCreateContactOpen}
          setIsOpen={setIsCreateContactOpen}
          resetPage={resetPartnerContacts}
          partnerId={partnerId}
        />
      )}
    </>
  );
};

export default PartnerContacts;
